/**
 * Reads the HTTP status out of a rejected RTK Query mutation.
 *
 * `unwrap()` rejects with a `FetchBaseQueryError` (`{ status, data }`), not an `Error`, so the
 * common `err instanceof Error` branch never matches and every failure collapses into one generic
 * message. The items API distinguishes 409 (id already taken) from 404 (row is gone), and the admin
 * needs to be told which.
 */
export const getErrorStatus = (err: unknown): number | undefined => {
  if (typeof err !== "object" || err === null) return undefined;
  const status = (err as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
};

/**
 * The explanation the server sent, when it sent one.
 *
 * `POST /orders` rejects a delivery with a message written for the shopper — "No entregamos en ese
 * municipio" — and collapsing that into a generic "algo salió mal" would throw away the only part
 * of the response that says how to fix it (EB-548).
 */
export const getErrorMessage = (err: unknown): string | null => {
  if (typeof err !== "object" || err === null) return null;
  const data = (err as { data?: unknown }).data;
  if (typeof data === "string") return data || null;
  if (typeof data !== "object" || data === null) return null;
  const message = (data as { error?: unknown }).error;
  return typeof message === "string" && message ? message : null;
};

export const HTTP_BAD_REQUEST = 400;
export const HTTP_CONFLICT = 409;
export const HTTP_NOT_FOUND = 404;
